import type {
  DiscrepancyGridAction,
  DiscrepancyGridRow,
  DiscrepancyState,
} from "./discrepancyGrid.interface";

export type RenderRow =
  | {
      kind: "group";
      id: string;
      /** The group's OWN id, unprefixed — what a toggle handler needs to key off. */
      groupId: string;
      label: string;
      summary?: string;
      state?: DiscrepancyState;
      actions?: DiscrepancyGridAction[];
      collapsed: boolean;
    }
  | { kind: "data"; id: string; row: DiscrepancyGridRow };

const isDiscrepancy = (row: DiscrepancyGridRow) => row.state !== "ok";

/**
 * Filters, then flattens grouped rows into ONE homogeneous list — group headers
 * become entries in the same array as data rows. Vibe's TableVirtualizedBody
 * takes a flat `items` array and renders whatever is in it, so a nested
 * group→rows shape could not be virtualized without re-flattening at render time.
 *
 * Group order is first-appearance order of each groupId; rows within a group keep
 * their input order even if the caller interleaved groups.
 */
export const flattenRows = (
  rows: DiscrepancyGridRow[],
  filter: "all" | "discrepancies",
  collapsedGroups?: ReadonlySet<string>,
): RenderRow[] => {
  const visible = filter === "discrepancies" ? rows.filter(isDiscrepancy) : rows;

  const ungrouped: RenderRow[] = [];
  const groupOrder: string[] = [];
  const byGroup = new Map<
    string,
    {
      label: string;
      summary?: string;
      state?: DiscrepancyState;
      actions?: DiscrepancyGridAction[];
      rows: DiscrepancyGridRow[];
    }
  >();

  visible.forEach((row) => {
    if (!row.groupId) {
      ungrouped.push({ kind: "data", id: row.id, row });
      return;
    }
    if (!byGroup.has(row.groupId)) {
      groupOrder.push(row.groupId);
      byGroup.set(row.groupId, {
        label: row.groupLabel ?? row.groupId,
        summary: row.groupSummary,
        // Taken from the group's FIRST row. Every row of a group reports the same values —
        // the adapter copies them onto each one — so first-wins and last-wins agree; first is
        // chosen only because it needs no second pass.
        state: row.groupState,
        actions: row.groupActions,
        rows: [],
      });
    }
    byGroup.get(row.groupId)!.rows.push(row);
  });

  const grouped = groupOrder.flatMap<RenderRow>((groupId) => {
    const group = byGroup.get(groupId)!;
    const collapsed = collapsedGroups?.has(groupId) ?? false;
    return [
      // Prefixed so a group header can never collide with a data row's id — the
      // ids become React keys and TableVirtualizedBody item ids.
      {
        kind: "group",
        id: `group:${groupId}`,
        groupId,
        label: group.label,
        summary: group.summary,
        state: group.state,
        actions: group.actions,
        collapsed,
      },
      // A collapsed group emits NO data rows at all, rather than emitting them hidden. The
      // virtualized body renders from this flat array into fixed-height slots, so a hidden
      // row would still consume its slot and leave a gap the height of the row.
      ...(collapsed
        ? []
        : group.rows.map<RenderRow>((row) => ({ kind: "data", id: row.id, row }))),
    ];
  });

  return [...ungrouped, ...grouped];
};
